import { useState, useCallback, useRef } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { IZoneForm } from '@/interfaces/Zone/zone.interface';
import convexHull from 'convex-hull';

type Point = { lat: number; lng: number };

const getPolygonCentroid = (points: Point[]): Point => {
    if (!points || points.length === 0) return { lat: 0, lng: 0 };
    let latSum = 0, lngSum = 0;
    points.forEach(p => { latSum += p.lat; lngSum += p.lng; });
    return { lat: latSum / points.length, lng: lngSum / points.length };
};

export const createConvexHullPolygon = (points: Point[]): Point[] => {
    if (points.length < 3) return points;

    const raw: [number, number][] = points.map(p => [p.lng, p.lat]);

    const edges: [number, number][] = convexHull(raw);

    if (!edges || edges.length === 0) return points;

    const ordered: Point[] = [];
    let currentIdx = edges[0][0];

    const nextMap = new Map<number, number>();
    edges.forEach(([a, b]) => nextMap.set(a, b));

    for (let i = 0; i < edges.length; i++) {
        ordered.push({ lat: raw[currentIdx][1], lng: raw[currentIdx][0] });
        const next = nextMap.get(currentIdx);
        if (next === undefined) break;
        currentIdx = next;
    }

    return ordered;
};

export const useZoneMapControls = (
    zoneType: string,
    polygon: Point[],
    setValue: UseFormSetValue<IZoneForm>,
    defaultLocation: Point
) => {
    const [markerPosition, setMarkerPosition] = useState(defaultLocation);
    const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox | null>(null);
    const [polygonError, setPolygonError] = useState<string | null>(null);

    const circleRef = useRef<google.maps.Circle | null>(null);
    const polygonRef = useRef<google.maps.Polygon | null>(null);

    const reverseGeocode = useCallback((lat: number, lng: number) => {
        if (window.google?.maps?.Geocoder) {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results?.[0]) {
                    const comps = results[0].address_components;
                    const cityObj = comps.find(c => c.types.includes('locality'));
                    const subObj = comps.find(c =>
                        c.types.includes('sublocality') || c.types.includes('neighborhood')
                    );
                    if (cityObj) setValue('city', cityObj.long_name, { shouldValidate: true });
                    if (subObj) setValue('area', subObj.long_name, { shouldValidate: true });
                }
            });
        }
    }, [setValue]);

    const applyHullAndSync = useCallback((rawCoords: Point[]) => {
        const hull = createConvexHullPolygon(rawCoords);

        if (polygonRef.current) {
            polygonRef.current.setPaths(hull);
        }

        setValue('polygon', hull, { shouldValidate: true });
        const centroid = getPolygonCentroid(hull);
        setValue('latitude', centroid.lat, { shouldValidate: true });
        setValue('longitude', centroid.lng, { shouldValidate: true });
        reverseGeocode(centroid.lat, centroid.lng);
        setPolygonError(null);

        return hull;
    }, [setValue, reverseGeocode]);

    const onCircleRadiusChanged = useCallback(() => {
        if (circleRef.current)
            setValue('radius', Math.round(circleRef.current.getRadius()), { shouldValidate: true });
    }, [setValue]);

    const onCircleLoad = useCallback((c: google.maps.Circle) => { circleRef.current = c; }, []);
    const onCircleUnmount = useCallback(() => { circleRef.current = null; }, []);

    const onPolygonLoad = useCallback((polygonInst: google.maps.Polygon) => {
        polygonRef.current = polygonInst;
        const path = polygonInst.getPath();

        const getCoords = (): Point[] => {
            const arr: Point[] = [];
            for (let i = 0; i < path.getLength(); i++)
                arr.push({ lat: path.getAt(i).lat(), lng: path.getAt(i).lng() });
            return arr;
        };

        const onVertexChange = () => {
            const coords = getCoords();
            if (coords.length >= 3) {
                applyHullAndSync(coords);
            } else {
                setValue('polygon', coords, { shouldValidate: true });
            }
        };

        const onVertexRemove = () => {
            const coords = getCoords();
            setValue('polygon', coords, { shouldValidate: true });
            if (coords.length >= 3) {
                const centroid = getPolygonCentroid(coords);
                setValue('latitude', centroid.lat, { shouldValidate: true });
                setValue('longitude', centroid.lng, { shouldValidate: true });
            }
        };

        const listeners = [
            path.addListener('set_at', onVertexChange),
            path.addListener('insert_at', onVertexChange),
            path.addListener('remove_at', onVertexRemove),
        ];
        (polygonInst as any)._listeners = listeners;
    }, [setValue, applyHullAndSync]);

    const onPolygonUnmount = useCallback((polygonInst: google.maps.Polygon) => {
        polygonRef.current = null;
        (polygonInst as any)._listeners?.forEach((l: any) => l.remove());
    }, []);

    const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (zoneType === 'polygon' && e.latLng) {
            const newPoint: Point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
            const allPoints = [...polygon, newPoint];

            if (allPoints.length < 3) {
                setValue('polygon', allPoints, { shouldValidate: true });
            } else {
                applyHullAndSync(allPoints);
            }
        }
    }, [zoneType, polygon, setValue, applyHullAndSync]);

    const onVertexDrag = useCallback((index: number, e: google.maps.MapMouseEvent) => {
        if (!e.latLng || !polygonRef.current) return;

        const path = polygonRef.current.getPath();
        const liveCoords: Point[] = [];
        for (let i = 0; i < path.getLength(); i++) {
            liveCoords.push({ lat: path.getAt(i).lat(), lng: path.getAt(i).lng() });
        }

        liveCoords[index] = { lat: e.latLng.lat(), lng: e.latLng.lng() };

        if (liveCoords.length >= 3) {
            applyHullAndSync(liveCoords);
        } else {
            setValue('polygon', liveCoords, { shouldValidate: true });
        }
    }, [applyHullAndSync, setValue]);

    const onMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
            const lat = e.latLng.lat(), lng = e.latLng.lng();
            setMarkerPosition({ lat, lng });
            setValue('latitude', lat, { shouldValidate: true });
            setValue('longitude', lng, { shouldValidate: true });
            reverseGeocode(lat, lng);
        }
    }, [setValue, reverseGeocode]);

    const onLoadSearchBox = useCallback((ref: google.maps.places.SearchBox) => {
        setSearchBox(ref);
    }, []);

    const onPlacesChanged = useCallback(() => {
        if (!searchBox) return;
        const places = searchBox.getPlaces();
        if (places?.length > 0) {
            const place = places[0];
            if (place.geometry?.location) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                setMarkerPosition({ lat, lng });
                setValue('latitude', lat, { shouldValidate: true });
                setValue('longitude', lng, { shouldValidate: true });
                const comps = place.address_components;
                if (comps) {
                    const cityObj = comps.find(c => c.types.includes('locality'));
                    const subObj = comps.find(c =>
                        c.types.includes('sublocality') || c.types.includes('neighborhood')
                    );
                    if (cityObj) setValue('city', cityObj.long_name, { shouldValidate: true });
                    if (subObj) setValue('area', subObj.long_name, { shouldValidate: true });
                }
            }
        }
    }, [searchBox, setValue]);

    return {
        markerPosition, setMarkerPosition,
        searchBox, setSearchBox,
        polygonError,
        onCircleRadiusChanged, onCircleLoad, onCircleUnmount,
        onPolygonLoad, onPolygonUnmount,
        onMapClick, onMarkerDragEnd, onVertexDrag,
        onLoadSearchBox, onPlacesChanged,
        reverseGeocode, getPolygonCentroid,
    };
};